const MEBIBYTE = 1024 * 1024;
const GIBIBYTE = 1024 * MEBIBYTE;

export const MIN_BROWSER_STORAGE_HEADROOM = 512 * MEBIBYTE;
export const MAX_BROWSER_STORAGE_HEADROOM = 5 * GIBIBYTE;
export const BROWSER_STORAGE_HEADROOM_RATIO = 0.05;

export interface BrowserStoragePressure {
  availableBytes: number;
  requiredBytes: number;
  pressured: boolean;
}

/**
 * Convert the browser's origin-scoped storage estimate into a conservative
 * early-warning signal. This is not a device-disk measurement: browsers do
 * not expose that information to websites. The quota does, however, shrink
 * when the browser can no longer grant the origin enough local headroom.
 */
export function assessBrowserStoragePressure(
  estimate: StorageEstimate
): BrowserStoragePressure | null {
  const { quota, usage } = estimate;
  if (
    typeof quota !== 'number' ||
    !Number.isFinite(quota) ||
    quota <= 0 ||
    typeof usage !== 'number' ||
    !Number.isFinite(usage) ||
    usage < 0
  ) {
    return null;
  }

  const availableBytes = Math.max(0, quota - usage);
  const requiredBytes = Math.min(
    MAX_BROWSER_STORAGE_HEADROOM,
    Math.max(
      MIN_BROWSER_STORAGE_HEADROOM,
      quota * BROWSER_STORAGE_HEADROOM_RATIO
    )
  );

  return {
    availableBytes,
    requiredBytes,
    pressured: availableBytes < requiredBytes,
  };
}
